import React, { useState, useEffect } from 'react';
import { Badge, Button, Form, InputGroup, Table } from 'react-bootstrap';
import ImageShow from "../Uitiles/ImageShow";
import Paginationnew from "../Uitiles/Paginationnew";
import { oneKlicks } from '../SDK/oneclick';
import { toast } from 'react-toastify';
import { getuserdata } from "../services/apiService";
import { Link } from 'react-router-dom';

const HospitalAppointmentlist = () => {
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [search, setSearch] = useState('');
    const [filtered, setFiltered] = useState([]);

    useEffect(() => {
        fetchAppointments();
    }, [page, limit]);

    useEffect(() => {
        // Filter appointments based on search
        if (search) {
            const searchLower = search.toLowerCase();
            const filtered = appointments.filter(apt => {
                const patient = apt.patient?.[0] || {};
                const doctor = apt.doctor?.[0] || {};
                return (
                    patient.display_name?.toLowerCase().includes(searchLower) ||
                    patient.email?.toLowerCase().includes(searchLower) ||
                    doctor.display_name?.toLowerCase().includes(searchLower) ||
                    apt.status?.toLowerCase().includes(searchLower) ||
                    apt.id?.toString().includes(searchLower)
                );
            });
            setFiltered(filtered);
        } else {
            setFiltered(appointments);
        }
    }, [search, appointments]);

    const fetchAppointments = async () => {
        try {
            setLoading(true);
            const hospitalData = await getuserdata("full");
            if (!hospitalData?.id) {
                console.log('No hospital data found');
                setLoading(false);
                return;
            }

            console.log('Fetching appointments for hospital:', hospitalData.id);

            // Try to fetch appointments directly by hospital_id first
            let appointmentsResponse = await oneKlicks.read(
                "",
                "appointments",
                { hospital_id: hospitalData.id },
                "id DESC",
                "",
                { page, limit },
                [
                    {
                        target_table: "users",
                        target_column: "id",
                        target_value: "patient_id",
                        target_label: "patient"
                    },
                    {
                        target_table: "users",
                        target_column: "id",
                        target_value: "doctor_id",
                        target_label: "doctor"
                    }
                ]
            );

            console.log('Direct hospital appointments response:', appointmentsResponse);

            // If no appointments found by hospital_id, try fetching by doctor IDs
            if (!appointmentsResponse?.data || appointmentsResponse.data.length === 0) {
                console.log('No direct appointments, trying via doctors...');

                // Get all doctors associated with this hospital
                const doctorsResponse = await oneKlicks.read(
                    "",
                    "hospital_doctors",
                    { hospital_id: hospitalData.id, status: "Active" },
                    "",
                    "",
                    "",
                    []
                );

                console.log('Hospital doctors response:', doctorsResponse);

                if (!doctorsResponse?.data || doctorsResponse.data.length === 0) {
                    console.log('No doctors found for this hospital');
                    setAppointments([]);
                    setFiltered([]);
                    setLoading(false);
                    return;
                }

                // Get all doctor IDs
                const doctorIds = doctorsResponse.data.map(d => d.doctor_id);
                console.log('Doctor IDs:', doctorIds);

                // Fetch appointments for these doctors
                const allAppointmentsResponse = await oneKlicks.read(
                    "",
                    "appointments",
                    {},
                    "id DESC",
                    "",
                    { page, limit: 100 }, // Fetch more to filter
                    [
                        {
                            target_table: "users",
                            target_column: "id",
                            target_value: "patient_id",
                            target_label: "patient"
                        },
                        {
                            target_table: "users",
                            target_column: "id",
                            target_value: "doctor_id",
                            target_label: "doctor"
                        }
                    ]
                );

                console.log('All appointments response:', allAppointmentsResponse);

                if (allAppointmentsResponse?.success && allAppointmentsResponse?.data) {
                    // Filter appointments by doctor IDs
                    const filteredAppointments = allAppointmentsResponse.data.filter(apt =>
                        doctorIds.includes(apt.doctor_id)
                    );

                    console.log('Filtered appointments:', filteredAppointments);
                    setAppointments(filteredAppointments);
                    setFiltered(filteredAppointments);
                    setTotalPages(Math.ceil(filteredAppointments.length / limit));
                } else {
                    setAppointments([]);
                    setFiltered([]);
                }
            } else {
                // Use direct hospital appointments
                console.log('Using direct hospital appointments:', appointmentsResponse.data);
                setAppointments(appointmentsResponse.data);
                setFiltered(appointmentsResponse.data);
                setTotalPages(appointmentsResponse.totalPages || 1);
            }
        } catch (error) {
            console.error("Error fetching appointments:", error);
            toast.error("Failed to fetch appointments");
            setAppointments([]);
            setFiltered([]);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return "N/A";
        try {
            const date = new Date(dateStr);
            if (isNaN(date.getTime())) return "N/A";
            return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        } catch (error) {
            return "N/A";
        }
    };

    const getStatusBadge = (status) => {
        const statusLower = (status || "").toLowerCase();
        let variant = "secondary";
        if (statusLower === "confirmed" || statusLower === "upcoming") variant = "success";
        else if (statusLower === "pending") variant = "warning";
        else if (statusLower === "completed" || statusLower === "done") variant = "info";
        else if (statusLower === "cancelled" || statusLower === "canceled") variant = "danger";

        return <Badge bg={variant}>{status || "Unknown"}</Badge>;
    };

    const handleDelete = async (appointment) => {
        if (!window.confirm('Are you sure you want to delete this appointment?')) {
            return;
        }

        try {
            const response = await oneKlicks.delete("", "appointments", appointment.id);
            if (response.success) {
                toast.success('Appointment deleted successfully');
                setAppointments(appointments.filter(a => a.id !== appointment.id));
                setFiltered(filtered.filter(a => a.id !== appointment.id));
            } else {
                toast.error('Failed to delete appointment');
            }
        } catch (error) {
            console.error('Error deleting appointment:', error);
            toast.error('Failed to delete appointment');
        }
    };

    return (
        <div className="dc-haslayout dc-dbsectionspace">
            <div className="container-fluid py-3">
                <div className="card mb-4">
                    <div className="card-body">
                        <div className="align-items-center justify-content-between mb-3">
                            <div className="d-flex align-items-center gap-2 mb-2">
                                <h5 className="mb-0 font-weight-bold mr-3">Hospital Appointments</h5>
                                <Badge bg="info">Manage All Appointments</Badge>
                            </div>
                            <div className="d-flex justify-content-between align-items-center" style={{ gap: 12 }}>
                                <InputGroup className="p-0" style={{ maxWidth: 400, flex: "1 1 auto" }}>
                                    <Form.Control
                                        placeholder="Search by patient, doctor, status..."
                                        value={search}
                                        onChange={(e) => setSearch(e.target.value)}
                                    />
                                </InputGroup>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="card">
                    <div className="card-body">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status" style={{ width: '2rem', height: '2rem', borderColor: 'var(--themecolor)', borderRightColor: 'transparent' }}>
                                    <span className="visually-hidden">Loading...</span>
                                </div>
                                <p className="mt-2 text-muted">Loading appointments...</p>
                            </div>
                        ) : (
                            <>
                                <div className="table-responsive">
                                    <Table hover size="sm" className="mb-0 align-middle">
                                        <thead className="bg-light">
                                            <tr>
                                                <th style={{ padding: '12px 15px', fontSize: '13px', fontWeight: '600' }}>ID</th>
                                                <th style={{ padding: '12px 15px', fontSize: '13px', fontWeight: '600' }}>Patient</th>
                                                <th style={{ padding: '12px 15px', fontSize: '13px', fontWeight: '600' }}>Doctor</th>
                                                <th style={{ padding: '12px 15px', fontSize: '13px', fontWeight: '600' }}>Date</th>
                                                <th style={{ padding: '12px 15px', fontSize: '13px', fontWeight: '600' }}>Time</th>
                                                <th style={{ padding: '12px 15px', fontSize: '13px', fontWeight: '600' }}>Status</th>
                                                <th style={{ padding: '12px 15px', fontSize: '13px', fontWeight: '600' }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {filtered.length === 0 && (
                                                <tr>
                                                    <td colSpan={7} className="text-center text-muted py-4">
                                                        {search ? 'No appointments found matching your search.' : 'No appointments found.'}
                                                    </td>
                                                </tr>
                                            )}
                                            {filtered.map((appointment) => {
                                                const patient = appointment.patient?.[0] || {};
                                                const doctor = appointment.doctor?.[0] || {};

                                                return (
                                                    <tr key={appointment.id}>
                                                        <td style={{ padding: '12px 15px', fontSize: '13px' }}>#{appointment.id}</td>
                                                        <td style={{ padding: '12px 15px' }}>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <ImageShow
                                                                    url={patient.profile_photo_url || '/default-avatar.png'}
                                                                    width={35}
                                                                    height={35}
                                                                    style={{
                                                                        objectFit: 'cover',
                                                                        borderRadius: '50%',
                                                                        border: '1px solid #eee'
                                                                    }}
                                                                />
                                                                <div>
                                                                    <div style={{ fontWeight: '600', fontSize: '13px' }}>
                                                                        {patient.display_name || patient.first_name + ' ' + patient.last_name || 'N/A'}
                                                                    </div>
                                                                    <div style={{ fontSize: '11px', color: '#999' }}>{patient.email || ''}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '12px 15px' }}>
                                                            <div className="d-flex align-items-center gap-2">
                                                                <ImageShow
                                                                    url={doctor.profile_photo_url || '/default-avatar.png'}
                                                                    width={35}
                                                                    height={35}
                                                                    style={{
                                                                        objectFit: 'cover',
                                                                        borderRadius: '50%',
                                                                        border: '1px solid #eee'
                                                                    }}
                                                                />
                                                                <div>
                                                                    <div style={{ fontWeight: '600', fontSize: '13px' }}>
                                                                        {doctor.display_name || 'N/A'}
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td style={{ padding: '12px 15px', fontSize: '13px' }}>{formatDate(appointment.appointment_date)}</td>
                                                        <td style={{ padding: '12px 15px', fontSize: '13px' }}>{appointment.appointment_time || "N/A"}</td>
                                                        <td style={{ padding: '12px 15px' }}>{getStatusBadge(appointment.status)}</td>
                                                        <td style={{ padding: '12px 15px' }}>
                                                            <div className="d-flex gap-2">
                                                                <Button size="sm" variant="outline-primary" onClick={() => window.alert('View functionality coming soon')}>
                                                                    View
                                                                </Button>
                                                                <Button size="sm" variant="outline-danger" onClick={() => handleDelete(appointment)}>
                                                                    Delete
                                                                </Button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </Table>
                                </div>

                                {filtered.length > 0 && (
                                    <div className="mt-3">
                                        <Paginationnew
                                            currentPage={page}
                                            totalPages={totalPages}
                                            handlePageChange={setPage}
                                        />
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default HospitalAppointmentlist;
